// The admin-gated `/admin/keys` page: every stored Anthropic key by owner and
// id, each with Remove, and the form that assigns a key of Loom's own
// Anthropic organization to a user or a project, the one place a key is set.
// Mirrors AccessPage: a `.loom-dashboard`-scoped shell and a server-owned
// forbidden state. Removing an assigned key switches it off at Anthropic
// first (`apps/server/src/http/anthropic-key.ts`), so that Remove asks first.
// A row that is not assigned (a key of another org, under a uuid) pays for
// no call: it reads "not in use", and its Remove just deletes it. In an env
// without the org's admin key the form gives way to a line that says so, and
// an assigned row has no Remove: nothing there could switch it off. An
// assigned key shows its last 30 days from Loom's own ledger, the total the
// list carries (`spendUsd`), or a muted dash when it has no figure (the
// ledger unread or metering off); a row that is not assigned shows none. The
// month to date (Zurich) reads the same way, and beside it the admin's monthly
// limit, a small field per assigned key (empty: none): past it Focus shows a
// banner, nothing is refused. The field is keyed by the stored limit, so a
// save shows what is stored.
//
// The key field is uncontrolled: the key reaches no React state and no DOM
// attribute, and is emptied once sent.

import { useId, useRef, useState, type FormEvent, type JSX } from "react";

import {
  ASSIGNABLE_KEY_NAME_PREFIX,
  AnthropicKeyOwnerSchema,
  SetAnthropicKeyLimitBodySchema,
  type AnthropicKeyOwner,
} from "@loom/shared/admin";

import { formatUsdDisplay } from "../analytics/usd";
import { useLocale, useT, type MessageKey, type Translator } from "../i18n/index.js";

import { useAnthropicKeysAdmin } from "./useAnthropicKeysAdmin";

interface AnthropicKeysAdminPageProps {
  readonly onBack: () => void;
}

type OwnerKind = AnthropicKeyOwner["kind"];

const ownerLabel = (t: Translator, owner: AnthropicKeyOwner): string =>
  owner.kind === "user"
    ? t("admin.keys.owner.user", { id: owner.userId })
    : t("admin.keys.owner.project", { id: owner.projectId });

/** The typed owner, or null when the id names none. */
const ownerOf = (kind: OwnerKind, id: string): AnthropicKeyOwner | null => {
  const raw = id.trim();
  const parsed = AnthropicKeyOwnerSchema.safeParse(
    kind === "user" ? { kind, userId: raw } : { kind, projectId: Number(raw) },
  );
  return parsed.success ? parsed.data : null;
};

/** The limit the field names (a comma is a decimal point): null clears it;
 *  undefined when it names no amount. */
const limitOf = (raw: string): string | null | undefined => {
  const value = raw.trim().replace(",", ".");
  const parsed = SetAnthropicKeyLimitBodySchema.shape.monthlyLimitUsd.safeParse(
    value === "" ? null : Number(value).toFixed(6),
  );
  return parsed.success ? parsed.data : undefined;
};

export const AnthropicKeysAdminPage = ({ onBack }: AnthropicKeysAdminPageProps): JSX.Element => {
  const t = useT();
  const { locale } = useLocale();
  const admin = useAnthropicKeysAdmin();
  const [kind, setKind] = useState<OwnerKind>("user");
  const [ownerId, setOwnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<MessageKey | null>(null);
  const keyRef = useRef<HTMLInputElement | null>(null);
  const assignHeadId = useId();

  const run = async (write: () => Promise<MessageKey | null>): Promise<void> => {
    setBusy(true);
    setNote(null);
    setNote(await write());
    setBusy(false);
  };

  const assign = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const field = keyRef.current;
    if (busy || field === null) return;
    const owner = ownerOf(kind, ownerId);
    if (owner === null) {
      setNote("admin.keys.error.ownerId");
      return;
    }
    const key = field.value;
    field.value = "";
    void run(() => admin.assign(owner, key));
  };

  const saveLimit = (event: FormEvent<HTMLFormElement>, owner: AnthropicKeyOwner): void => {
    event.preventDefault();
    if (busy) return;
    const limit = limitOf(String(new FormData(event.currentTarget).get("limit") ?? ""));
    if (limit === undefined) {
      setNote("admin.keys.error.limit");
      return;
    }
    void run(() => admin.setLimit(owner, limit));
  };

  const shell = (body: JSX.Element): JSX.Element => (
    <div className="loom-dashboard admin-keys-page">
      <header className="admin-access-head">
        <button className="admin-access-back" onClick={onBack} type="button">
          ← {t("admin.home.back")}
        </button>
        <h1 className="admin-access-title">{t("admin.keys.title")}</h1>
      </header>
      {body}
    </div>
  );

  if (admin.load === "forbidden") {
    return shell(
      <div className="admin-access-state admin-access-denied">
        <h2>{t("admin.access.denied.title")}</h2>
        <p>{t("admin.access.denied.body")}</p>
      </div>,
    );
  }
  if (admin.load === "failed") {
    return shell(
      <p className="admin-access-state admin-access-state-error">{t("admin.keys.loadFailed")}</p>,
    );
  }
  if (admin.load === "loading") {
    return shell(<p className="admin-access-state">{t("admin.keys.loading")}</p>);
  }

  return shell(
    <>
      {note !== null ? (
        <p className="admin-access-state-error" role="alert">
          {t(note, { prefix: ASSIGNABLE_KEY_NAME_PREFIX })}
        </p>
      ) : null}
      {admin.keys.length ? (
        <table className="admin-access-table">
          <thead>
            <tr>
              <th>{t("admin.keys.col.owner")}</th>
              <th>{t("admin.keys.col.keyId")}</th>
              <th>{t("admin.keys.col.spend")}</th>
              <th>{t("admin.keys.col.month")}</th>
              <th>{t("admin.keys.col.limit")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {admin.keys.map((row) => {
              const label = ownerLabel(t, row.owner);
              return (
                <tr key={row.keyId}>
                  <td>{label}</td>
                  <td>
                    <span className="admin-keys-id">{row.keyId}</span>
                    {row.assigned ? null : (
                      <div className="admin-access-muted">{t("admin.keys.notInUse")}</div>
                    )}
                  </td>
                  <td>
                    {row.spendUsd !== null ? (
                      formatUsdDisplay(row.spendUsd, locale)
                    ) : row.assigned ? (
                      <span className="admin-access-muted">—</span>
                    ) : null}
                  </td>
                  <td>
                    {row.monthUsd != null ? (
                      formatUsdDisplay(row.monthUsd, locale)
                    ) : row.assigned ? (
                      <span className="admin-access-muted">—</span>
                    ) : null}
                  </td>
                  <td>
                    {row.assigned ? (
                      <form
                        key={row.monthlyLimitUsd ?? ""}
                        className="admin-keys-limit"
                        onSubmit={(e) => saveLimit(e, row.owner)}
                      >
                        <input
                          name="limit"
                          className="admin-access-add-search"
                          inputMode="decimal"
                          defaultValue={
                            row.monthlyLimitUsd != null ? String(Number(row.monthlyLimitUsd)) : ""
                          }
                          aria-label={t("admin.keys.limit.field", { owner: label })}
                        />
                        <button type="submit" className="admin-access-add-save" disabled={busy}>
                          {t("common.save")}
                        </button>
                      </form>
                    ) : null}
                  </td>
                  <td>
                    {row.assigned && !admin.assignable ? null : (
                      <button
                        type="button"
                        className="admin-access-grant-remove"
                        aria-label={t("admin.keys.removeOwner", { owner: label })}
                        disabled={busy}
                        onClick={() => {
                          if (
                            row.assigned &&
                            !window.confirm(t("admin.keys.unassignConfirm", { owner: label }))
                          ) {
                            return;
                          }
                          void run(() => admin.unassign(row.owner));
                        }}
                      >
                        {t("common.remove")}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="admin-access-muted">{t("admin.keys.empty")}</p>
      )}
      {admin.assignable ? (
        <section className="admin-keys-assign" aria-labelledby={assignHeadId}>
          <h2 id={assignHeadId}>{t("admin.keys.assign.title")}</h2>
          <form className="admin-access-add" onSubmit={assign}>
            <label className="admin-access-add-field">
              <span>{t("admin.keys.col.owner")}</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value === "project" ? "project" : "user")}
              >
                <option value="user">{t("admin.keys.kind.user")}</option>
                <option value="project">{t("admin.keys.kind.project")}</option>
              </select>
            </label>
            <label className="admin-access-add-field">
              <span>{t("admin.keys.assign.id")}</span>
              <input
                className="admin-access-add-search"
                inputMode="numeric"
                required
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
              />
            </label>
            <label className="admin-access-add-field">
              <span>{t("account.anthropic.field")}</span>
              <input
                ref={keyRef}
                className="admin-access-add-search"
                type="password"
                autoComplete="off"
                spellCheck={false}
                required
              />
            </label>
            <div className="admin-access-add-actions">
              <button type="submit" className="admin-access-add-save" disabled={busy}>
                {busy ? t("account.anthropic.checking") : t("admin.keys.assign.submit")}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <p className="admin-access-muted">{t("admin.keys.off")}</p>
      )}
    </>,
  );
};
